import { ConstructionStatusEnum } from "@/info/unit/constructionStatus";
import { Filters } from "@/types/filters";
import { createDefaultFilters } from "@/utils/search-url-transform";

/**
 * Creates default form values for filter components
 * Uses the existing createDefaultFilters utility for consistency
 */
export function createFormDefaults(): Partial<Filters> {
	return createDefaultFilters();
}

/**
 * Checks if a dirty field has a truly empty/meaningless value
 * This function determines whether a field that has been interacted with
 * should be excluded from the final filter payload
 */
export function isDirtyFieldEmpty(key: keyof Filters, value: unknown): boolean {
	// Empty arrays (except locations which are handled specially)
	if (Array.isArray(value) && value.length === 0 && key !== "locations") {
		return true;
	}

	// Empty strings
	if (typeof value === "string" && value.trim() === "") {
		return true;
	}

	// Zero range values {min: 0, max: 0}
	if (typeof value === "object" && value !== null && "min" in value && "max" in value) {
		const rangeValue = value as { min: number; max: number };
		if (rangeValue.min === 0 && rangeValue.max === 0) {
			return true;
		}
	}

	// Zero numeric values for specific fields
	if (typeof value === "number" && value === 0) {
		const zeroSkipFields = ["bathroomsMin", "builtupAreaMin", "balconiesMin", "parkingsMin"];
		if (zeroSkipFields.includes(key as string)) {
			return true;
		}
	}

	// Default plot area value (400) should be treated as empty unless explicitly set by user
	if (key === "plotAreaMin" && typeof value === "number" && value === 400) {
		return true;
	}

	return false;
}

/**
 * Creates a function to mark fields as manually dirty
 * This is needed because React Hook Form's dirty detection is unreliable
 */
export function createMarkFieldDirty(manualDirtyFields: React.RefObject<Record<string, boolean>>) {
	return (fieldName: string) => {
		manualDirtyFields.current[fieldName] = true;
	};
}

/**
 * Determines if plot facing should be disabled based on property type
 */
export function isPlotFacingDisabled(propertyType: string): boolean {
	return propertyType === "apartment";
}

/**
 * Determines if max age section should be disabled based on status
 */
export function isMaxAgeDisabled(status: string): boolean {
	return status !== ConstructionStatusEnum.ReadyToMoveIn;
}

/**
 * Determines if possession section should be disabled based on status
 */
export function isPossessionDisabled(status: string): boolean {
	return status !== ConstructionStatusEnum.UnderConstruction;
}

/**
 * Handles the logic for auto-setting/clearing maxAge and possession based on status
 */
export function handleStatusChange(
	status: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setValue: (name: keyof Filters, value: any, options?: { shouldDirty: boolean }) => void,
) {
	if (status === ConstructionStatusEnum.ReadyToMoveIn) {
		setValue("possession", "", { shouldDirty: true });
		setValue("maxAge", "max:15", { shouldDirty: true });
	} else if (status === ConstructionStatusEnum.UnderConstruction) {
		setValue("maxAge", "", { shouldDirty: true });
		setValue("possession", "preLaunch", { shouldDirty: true });
	} else {
		setValue("maxAge", "", { shouldDirty: true });
		setValue("possession", "", { shouldDirty: true });
	}
}

/**
 * Handles the logic for plot facing and plot area based on property type
 */
export function handlePropertyTypeChange({
	propertyType,
	plotAreaMin,
	setValue,
}: {
	propertyType?: string;
	plotAreaMin?: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setValue: (name: keyof Filters, value: any, options?: { shouldDirty: boolean }) => void;
}) {
	const isApartment = propertyType === "apartment";

	if (isApartment) {
		setValue("plotFacing", [], { shouldDirty: true });
		setValue("plotAreaMin", 0, { shouldDirty: true });
	} else if (propertyType && ["v-", "h-", "p-", "villa", "house", "plot"].includes(propertyType)) {
		if (!plotAreaMin || plotAreaMin < 400) {
			setValue("plotAreaMin", 400, { shouldDirty: true });
		}
	}
}

export function createClearFiltersHandler({
	reset,
	manualDirtyFields,
	setFilters,
}: {
	reset: (values?: Partial<Filters>) => void;
	manualDirtyFields: React.MutableRefObject<Record<string, boolean>>;
	setFilters: (filters: Partial<Filters>) => void;
}) {
	return () => {
		const defaultFilters = createFormDefaults();
		reset(defaultFilters);
		// Clear manual dirty tracking
		manualDirtyFields.current = {};
		// Clear URL-based filters
		setFilters({});
	};
}
